import React, { useState, useEffect, useRef } from 'react';

const SYMBOLS = [
  { id:'bitcoin',       symbol:'BTC',  icon:'₿'  },
  { id:'ethereum',      symbol:'ETH',  icon:'Ξ'  },
  { id:'binancecoin',   symbol:'BNB',  icon:'BNB' },
  { id:'solana',        symbol:'SOL',  icon:'SOL' },
  { id:'tether',        symbol:'USDT', icon:'₮'  },
  { id:'dogecoin',      symbol:'DOGE', icon:'Ð'  },
  { id:'tron',          symbol:'TRX',  icon:'TRX' },
];

// USD to NGN rate (updated dynamically)
const USD_NGN_DEFAULT = 1420;

function fmt(naira) {
  if (naira >= 1_000_000) return '₦' + (naira/1_000_000).toFixed(2) + 'M';
  if (naira >= 1_000)     return '₦' + (naira/1_000).toFixed(1) + 'K';
  return '₦' + naira.toFixed(2);
}

export default function LiveTicker() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usdNgn, setUsdNgn] = useState(USD_NGN_DEFAULT);
  const tickerRef = useRef(null);

  async function fetchPrices() {
    try {
      // Fetch crypto prices + NGN rate together
      const ids = [...SYMBOLS.map(s => s.id), 'usd-coin'].join(',');
      const res = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd,ngn&include_24hr_change=true`,
        { headers: { 'Accept': 'application/json' } }
      );
      const data = await res.json();

      // Get USD/NGN rate from NGN price of USDC
      const usdcNgn = data['usd-coin']?.ngn;
      const rate = usdcNgn ? Math.round(usdcNgn) : USD_NGN_DEFAULT;
      setUsdNgn(rate);

      const arr = SYMBOLS.map(s => ({
        ...s,
        usd: data[s.id]?.usd || 0,
        ngn: data[s.id]?.ngn || (data[s.id]?.usd || 0) * rate,
        change: data[s.id]?.usd_24h_change || 0,
      })).filter(s => s.usd > 0);
      setPrices(arr);
      setLoading(false);
    } catch(e) {
      console.log('[TICKER] fetch failed:', e.message);
    }
  }

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  if (loading || prices.length === 0) return null;

  // Duplicate for seamless loop
  const items = [...prices, ...prices];

  return (
    <div style={{
      background: 'var(--bg1)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'hidden',
      height: 36,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      marginBottom: 16,
      width: '100%',
      contain: 'strict',
    }}>
      {/* Live badge */}
      <div style={{
        flexShrink: 0,
        padding: '0 10px',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        height: '100%',
        background: 'var(--bg1)',
        zIndex: 2,
      }}>
        <div style={{ width:6, height:6, borderRadius:'50%', background:'#00c566', animation:'pulse 2s infinite' }}/>
        <span style={{ fontSize:10, fontWeight:700, color:'var(--muted)', letterSpacing:.5 }}>LIVE</span>
      </div>

      {/* Scrolling ticker */}
      <div ref={tickerRef} style={{
        display: 'flex',
        alignItems: 'center',
        animation: 'ticker 40s linear infinite',
        whiteSpace: 'nowrap',
        gap: 0,
        willChange: 'transform',
        flexShrink: 0,
      }}>
        {items.map((p, i) => (
          <div key={i} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 20px',
            borderRight: '1px solid var(--border)',
            height: 32,
          }}>
            <span style={{ fontSize:11, fontWeight:700, color:'var(--text2)' }}>{p.symbol}</span>
            <span style={{ fontSize:11, fontWeight:600, color:'var(--text)' }}>{fmt(p.ngn)}</span>
            <span style={{
              fontSize:10, fontWeight:600,
              color: p.change >= 0 ? '#00c566' : '#ff4444',
            }}>
              {p.change >= 0 ? '▲' : '▼'} {Math.abs(p.change).toFixed(2)}%
            </span>
          </div>
        ))}
      </div>

      {/* USD/NGN rate */}
      <div style={{
        position:'absolute', right:0, top:0, bottom:0,
        padding:'0 10px',
        borderLeft:'1px solid var(--border)',
        display:'flex', alignItems:'center', gap:5,
        background:'var(--bg1)', zIndex:2, flexShrink:0,
      }}>
        <span style={{ fontSize:10, color:'var(--muted)' }}>USD/NGN</span>
        <span style={{ fontSize:10, fontWeight:700, color:'var(--gold)' }}>₦{usdNgn.toLocaleString()}</span>
      </div>

      <style>{`
        @keyframes ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
      `}</style>
    </div>
  );
}
